import React, { useContext, useState } from 'react';
import { Box, Grid, Typography, Card, Divider, TextField, IconButton, Chip, Tooltip } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { ContractContext } from "../../../provider/ContractProvider";

const RectificationPage = () => {
    const { contract } = useContext(ContractContext);

    return (
        <Box sx={{
            // WICHTIG: width weg oder border-box nutzen, sonst addiert sich padding zur Breite -> Scrollbalken
            width: '100%',
            boxSizing: 'border-box',
            padding: 4,
            backgroundColor: '#f5f5f5',
            minHeight: '100vh',
            overflowX: 'hidden' // Safety net
        }}>
            <Typography variant="h4" sx={{ marginBottom: 4, fontWeight: 'bold' }}>
                Rectification Review
            </Typography>

            <Grid container spacing={4} sx={{marginBottom: 2}}>
                <Grid item xs={6}>
                    <Typography variant="h6" color="textSecondary">ORIGINAL CONTRACT</Typography>
                </Grid>
                <Grid item xs={6}>
                    <Typography variant="h6" color="primary">RECTIFIED VERSION</Typography>
                </Grid>
            </Grid>

            {contract.paragraphs.map((paragraph) => (
                <Box key={paragraph._id} sx={{ marginBottom: 6 }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', marginBottom: 2, borderBottom: '1px solid #ddd', paddingBottom: 1 }}>
                        {paragraph.content}
                    </Typography>

                    {paragraph.clauses.map((clause) => (
                        <RectificationItem key={clause._id} clause={clause} />
                    ))}
                </Box>
            ))}
        </Box>
    );
};

const RectificationItem = ({ clause }) => {
    const { updateCorrection } = useContext(ContractContext);

    const hasCorrection = !!clause.correction;
    const [isEditing, setIsEditing] = useState(false);
    const [editedText, setEditedText] = useState(clause.correction?.text || '');

    const handleSave = async () => {
        await updateCorrection(clause.correction._id, editedText);
        setIsEditing(false);
    };

    return (
        <Grid container spacing={4} sx={{ marginBottom: 4, alignItems: 'flex-start' }}>

            {/* LEFT: Original Clause */}
            <Grid item xs={6}>
                <Card sx={{
                    padding: 2,
                    backgroundColor: hasCorrection ? '#fff5f5' : 'white',
                    borderLeft: hasCorrection ? '4px solid #d32f2f' : '4px solid transparent',
                    opacity: hasCorrection ? 0.8 : 1,
                    position: 'relative',
                    wordBreak: 'break-word' // Verhindert Layout-Sprengung durch lange Wörter
                }}>
                    <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                        {clause.content}
                    </Typography>
                    {hasCorrection && (
                        <Chip
                            label="VOID"
                            size="small"
                            color="error"
                            sx={{ marginTop: 2, fontWeight: 'bold' }}
                        />
                    )}
                </Card>
            </Grid>

            {/* RIGHT: Rectification */}
            <Grid item xs={6}>
                {hasCorrection ? (
                    <Card sx={{
                        padding: 2,
                        border: '2px solid #2e7d32',
                        backgroundColor: '#f1f8e9',
                        display: 'flex',
                        flexDirection: 'column',
                        wordBreak: 'break-word' // Auch hier wichtig
                    }}>

                        {/* 1. SECTION: CORRECTED TEXT */}
                        <Box sx={{ marginBottom: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 1 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#2e7d32' }}>
                                    KORRIGIERTE FASSUNG
                                </Typography>
                                {!isEditing && (
                                    <Tooltip title="Text bearbeiten">
                                        <IconButton onClick={() => setIsEditing(true)} size="small">
                                            <EditIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </Box>

                            {isEditing ? (
                                <Box>
                                    <TextField
                                        fullWidth
                                        multiline
                                        minRows={3}
                                        variant="outlined"
                                        value={editedText}
                                        onChange={(e) => setEditedText(e.target.value)}
                                        sx={{ backgroundColor: 'white' }}
                                    />
                                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', marginTop: 1 }}>
                                        <IconButton onClick={handleSave} color="primary">
                                            <SaveIcon />
                                        </IconButton>
                                    </Box>
                                </Box>
                            ) : (
                                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', fontWeight: 500, padding: 1, backgroundColor: 'rgba(255,255,255,0.5)', borderRadius: 1 }}>
                                    {clause.correction.text}
                                </Typography>
                            )}
                        </Box>

                        <Divider sx={{ my: 1 }} />

                        {/* 2. SECTION: EXPLANATION */}
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', marginTop: 1 }}>
                            <AutoFixHighIcon color="primary" sx={{ marginRight: 1, marginTop: 0.5 }} fontSize="small" />
                            <Box>
                                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#1565c0' }}>
                                    AI Reasoning
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#555', marginTop: 0.5, fontSize: '0.9rem', whiteSpace: 'pre-wrap' }}>
                                    {clause.correction.explanation}
                                </Typography>
                            </Box>
                        </Box>

                        {!isEditing && (
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', marginTop: 2 }}>
                                <Chip
                                    icon={<CheckCircleOutlineIcon />}
                                    label="RECTIFIED"
                                    size="small"
                                    color="success"
                                    variant="outlined"
                                />
                            </Box>
                        )}
                    </Card>
                ) : (
                    // Valid Placeholder
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        paddingLeft: 2,
                        paddingTop: 2,
                        opacity: 0.4
                    }}>
                        <CheckCircleOutlineIcon fontSize="small" sx={{ mr: 1 }} />
                        <Typography variant="body2" color="textSecondary">
                            Valid. No changes.
                        </Typography>
                    </Box>
                )}
            </Grid>
        </Grid>
    );
};

export default RectificationPage;